/*  CaseValidationInbox — Admin review queue for course case submissions.
    ────────────────────────────────────────────────────────────────────────
    Route:  /admin/case-validations
    Access: admin + super_admin.

    Tabs: Pending · Approved · Rejected.
    Row → detail modal with the doctor's write-up, attachments, and
    approve / reject buttons.  Approving a submission that also has all
    lessons/quizzes/final-exam completed triggers auto-issuance of the
    doctor's course certificate (server-side).
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ClipboardText, ClockCounterClockwise, CheckCircle, XCircle,
    User as UserIcon, FileArrowDown, Paperclip, ArrowRight,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { confirmDialog } from "@/lib/confirm";
import RichTextView from "@/components/creator/RichTextView";


function timeAgo(iso) {
    if (!iso) return "—";
    const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return `${Math.round(secs / 86400)}d ago`;
}


export default function CaseValidationInbox() {
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState({ pending: [], approved: [], rejected: [] });
    const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, a, r] = await Promise.all([
                api.get("/admin/case-validations?status=pending"),
                api.get("/admin/case-validations?status=approved"),
                api.get("/admin/case-validations?status=rejected"),
            ]);
            setRows({
                pending: p.data.items || [],
                approved: a.data.items || [],
                rejected: r.data.items || [],
            });
            setCounts(p.data.counts || { pending: 0, approved: 0, rejected: 0 });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load case validations.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const prev = document.title;
        document.title = "Case Validations — Admin";
        return () => { document.title = prev; };
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8" data-testid="case-validation-inbox">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Admin</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5 flex items-center gap-2">
                        <ClipboardText size={28} weight="duotone" className="text-primary" />
                        Case Validations
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Review clinical case write-ups from doctors on courses that require case validation. Approve to release their certificate; reject with feedback to request revisions.
                    </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <StatChip label="Pending" value={counts.pending} tone="amber" Icon={ClockCounterClockwise} />
                    <StatChip label="Approved" value={counts.approved} tone="emerald" Icon={CheckCircle} />
                    <StatChip label="Rejected" value={counts.rejected} tone="rose" Icon={XCircle} />
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-6">
                <TabsList className="h-10 bg-secondary/60 border border-border">
                    <TabsTrigger value="pending" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="case-tab-pending">
                        <ClockCounterClockwise size={13} weight="duotone" /> Pending
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{counts.pending}</span>
                    </TabsTrigger>
                    <TabsTrigger value="approved" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="case-tab-approved">
                        <CheckCircle size={13} weight="duotone" /> Approved
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{counts.approved}</span>
                    </TabsTrigger>
                    <TabsTrigger value="rejected" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="case-tab-rejected">
                        <XCircle size={13} weight="duotone" /> Rejected
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{counts.rejected}</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="pending" data-testid="pane-case-pending">
                    <RowList rows={rows.pending} loading={loading} onOpen={setOpenId} emptyTitle="Inbox zero" emptyMessage="No case submissions awaiting review." />
                </TabsContent>
                <TabsContent value="approved" data-testid="pane-case-approved">
                    <RowList rows={rows.approved} loading={loading} onOpen={setOpenId} emptyTitle="No approvals yet" emptyMessage="Once you approve a case submission, it lands here for audit." />
                </TabsContent>
                <TabsContent value="rejected" data-testid="pane-case-rejected">
                    <RowList rows={rows.rejected} loading={loading} onOpen={setOpenId} emptyTitle="No rejections" emptyMessage="Rejected submissions surface here alongside the feedback you left." />
                </TabsContent>
            </Tabs>

            {openId && (
                <CaseDetailDrawer
                    submissionId={openId}
                    onClose={() => setOpenId(null)}
                    onDecided={async () => { setOpenId(null); await load(); }}
                />
            )}
        </div>
    );
}


function StatChip({ label, value, tone, Icon }) {
    const cls = {
        amber:   "bg-amber-50 border-amber-200 text-amber-800",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
        rose:    "bg-rose-50 border-rose-200 text-rose-800",
    }[tone] || "bg-secondary border-border text-foreground";
    return (
        <div className={`px-3 py-2 rounded-xl border ${cls} min-w-24`} data-testid={`case-stat-${label.toLowerCase()}`}>
            <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider">
                <Icon size={9} weight="fill" /> {label}
            </div>
            <div className="text-xl font-black tabular-nums leading-none mt-1">{value}</div>
        </div>
    );
}


function RowList({ rows, loading, onOpen, emptyTitle, emptyMessage }) {
    if (loading) {
        return (
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="px-4 py-4 border-b border-border last:border-b-0 flex items-center gap-4 animate-pulse">
                        <div className="flex-1 space-y-2">
                            <div className="h-3 bg-secondary rounded w-2/3" />
                            <div className="h-2.5 bg-secondary/70 rounded w-1/3" />
                        </div>
                        <div className="h-6 w-20 bg-secondary rounded-md" />
                    </div>
                ))}
            </div>
        );
    }
    if (rows.length === 0) {
        return (
            <div className="py-16 text-center border border-dashed border-border rounded-2xl" data-testid="case-empty-state">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 mx-auto mb-3 grid place-items-center">
                    <CheckCircle size={26} weight="duotone" className="text-emerald-600" />
                </div>
                <h3 className="font-display text-lg font-bold">{emptyTitle}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{emptyMessage}</p>
            </div>
        );
    }
    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div
                className="grid items-center px-4 py-3 border-b border-border bg-secondary/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                style={{ gridTemplateColumns: "1fr 200px 130px 110px 130px" }}
            >
                <div>Course</div>
                <div>Doctor</div>
                <div>Attachments</div>
                <div>Submitted</div>
                <div className="text-right"></div>
            </div>
            {rows.map((r) => (
                <div
                    key={r.id}
                    className="grid items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors"
                    style={{ gridTemplateColumns: "1fr 200px 130px 110px 130px" }}
                    data-testid={`case-row-${r.id}`}
                >
                    <div className="min-w-0">
                        <div className="font-semibold text-sm truncate" data-testid={`case-row-title-${r.id}`}>{r.course_title || "Untitled course"}</div>
                        {r.admin_notes && (
                            <div className="mt-0.5 text-[11px] text-muted-foreground italic line-clamp-1">
                                &ldquo;{r.admin_notes}&rdquo;
                            </div>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 truncate">
                        <UserIcon size={11} weight="duotone" />
                        <span className="truncate">{r.doctor_name || r.doctor_email || "Unknown"}</span>
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                        <Paperclip size={11} weight="duotone" />
                        {r.attachment_count} file{r.attachment_count === 1 ? "" : "s"}
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">{timeAgo(r.submitted_at)}</div>
                    <div className="text-right">
                        <button
                            type="button"
                            onClick={() => onOpen(r.id)}
                            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-colors"
                            data-testid={`case-row-open-${r.id}`}
                        >
                            Review <ArrowRight size={11} weight="bold" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}


function CaseDetailDrawer({ submissionId, onClose, onDecided }) {
    const [detail, setDetail] = useState(null);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/case-validations/${submissionId}`);
                if (!cancelled) {
                    setDetail(data);
                    setNotes(data?.submission?.admin_notes || "");
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't load submission.");
                onClose();
            }
        })();
        return () => { cancelled = true; };
    }, [submissionId, onClose]);

    const sub = detail?.submission;
    const course = detail?.course;
    const isDecided = sub && sub.status !== "pending";

    const attachmentSrc = useCallback((att) => {
        const token = localStorage.getItem("token");
        // The API path is /api/admin/case-validations/attachments/{id}/download
        return `${API}/admin/case-validations/attachments/${att.id}/download?_t=${Date.now()}`;
    }, []);

    const approve = useCallback(async () => {
        const ok = await confirmDialog({
            title: "Approve this case?",
            message: "The doctor will receive an approval notification and — if they've completed all other course criteria — their certificate will be issued.",
            confirmText: "Approve",
            cancelText: "Not yet",
            tone: "primary",
            testid: "case-approve-confirm",
        });
        if (!ok) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/case-validations/${submissionId}/approve`, { admin_notes: notes.trim() });
            if (data?.certificate) {
                toast.success("Case approved — certificate issued!");
            } else {
                toast.success("Case approved.");
            }
            await onDecided();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't approve.");
        } finally {
            setBusy(false);
        }
    }, [submissionId, notes, onDecided]);

    const reject = useCallback(async () => {
        const trimmed = notes.trim();
        if (trimmed.length < 10) {
            toast.error("Please leave at least 10 characters of feedback so the doctor knows what to revise.");
            return;
        }
        const ok = await confirmDialog({
            title: "Reject this case?",
            message: "The doctor will be notified with your feedback and can resubmit. Their certificate will remain locked until you approve a follow-up submission.",
            confirmText: "Reject",
            cancelText: "Cancel",
            tone: "warn",
            testid: "case-reject-confirm",
        });
        if (!ok) return;
        setBusy(true);
        try {
            await api.post(`/admin/case-validations/${submissionId}/reject`, { admin_notes: trimmed });
            toast.success("Case rejected. Doctor has been notified.");
            await onDecided();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reject.");
        } finally {
            setBusy(false);
        }
    }, [submissionId, notes, onDecided]);

    return (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex justify-end" data-testid="case-detail-drawer" onClick={onClose}>
            <div className="w-full max-w-2xl h-full bg-card shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {!sub ? (
                    <div className="p-8 text-center text-muted-foreground">Loading submission…</div>
                ) : (
                    <>
                        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 z-10">
                            <div className="min-w-0">
                                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Case Submission</div>
                                <h2 className="font-display text-xl font-bold mt-0.5 truncate">{sub.course_title || course?.title || "Case"}</h2>
                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                    <StatusPill status={sub.status} />
                                    <span>{sub.doctor_name || sub.doctor_email}</span>
                                    <span>·</span>
                                    <span>Submitted {timeAgo(sub.submitted_at)}</span>
                                </div>
                            </div>
                            <button type="button" onClick={onClose} className="text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="case-drawer-close">Close</button>
                        </div>

                        <div className="p-6 space-y-6">
                            {course?.case_validation_notes && (
                                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary">Author&apos;s brief</div>
                                    <RichTextView html={course.case_validation_notes} className="text-sm mt-1.5" />
                                </div>
                            )}

                            <Section label="Patient summary" value={sub.patient_summary} />
                            <Section label="Treatment summary" value={sub.treatment_summary} />
                            <Section label="Outcome summary" value={sub.outcome_summary} />
                            {sub.complications && <Section label="Complications" value={sub.complications} />}

                            <div>
                                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2">
                                    Attachments ({sub.attachments?.length || 0})
                                </div>
                                {(!sub.attachments || sub.attachments.length === 0) ? (
                                    <div className="text-sm text-muted-foreground italic">No attachments uploaded.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {sub.attachments.map((a) => (
                                            <a
                                                key={a.id}
                                                href={attachmentSrc(a)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-secondary/40 hover:bg-secondary transition-colors no-underline text-foreground"
                                                data-testid={`case-attachment-${a.id}`}
                                            >
                                                <FileArrowDown size={16} weight="duotone" className="text-primary" />
                                                <span className="text-sm font-semibold truncate flex-1">{a.filename}</span>
                                                <span className="text-[10px] uppercase text-muted-foreground tabular-nums">
                                                    {(a.size_bytes / 1024).toFixed(0)} KB
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {isDecided && sub.reviewed_by_name && (
                                <div className={`rounded-lg border p-4 ${sub.status === "approved" ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                                    <div className="text-[10px] font-bold uppercase tracking-wider">
                                        {sub.status === "approved" ? "Approved" : "Rejected"} by {sub.reviewed_by_name} · {timeAgo(sub.reviewed_at)}
                                    </div>
                                    {sub.admin_notes && (
                                        <div className="text-sm mt-1.5 whitespace-pre-wrap">{sub.admin_notes}</div>
                                    )}
                                </div>
                            )}

                            {!isDecided && (
                                <div className="rounded-lg border border-border p-4 bg-secondary/30 space-y-3">
                                    <label className="block">
                                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">
                                            Notes / feedback for the doctor
                                        </div>
                                        <Textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Optional on approval. Required (≥10 chars) on rejection so the doctor knows what to fix."
                                            rows={4}
                                            className="text-sm"
                                            data-testid="case-review-notes"
                                        />
                                    </label>
                                    <div className="flex items-center gap-2 justify-end">
                                        <Button
                                            variant="outline"
                                            onClick={reject}
                                            disabled={busy}
                                            className="border-rose-300 text-rose-700 hover:bg-rose-50"
                                            data-testid="case-reject-btn"
                                        >
                                            <XCircle size={14} weight="fill" className="mr-1.5" />
                                            Reject with feedback
                                        </Button>
                                        <Button
                                            onClick={approve}
                                            disabled={busy}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            data-testid="case-approve-btn"
                                        >
                                            <CheckCircle size={14} weight="fill" className="mr-1.5" />
                                            Approve
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}


function Section({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1.5">{label}</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{value || <span className="italic text-muted-foreground">(empty)</span>}</div>
        </div>
    );
}


function StatusPill({ status }) {
    const cls = useMemo(() => {
        if (status === "approved") return "bg-emerald-100 text-emerald-800 border-emerald-200";
        if (status === "rejected") return "bg-rose-100 text-rose-800 border-rose-200";
        return "bg-amber-100 text-amber-800 border-amber-200";
    }, [status]);
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${cls}`}>
            {status}
        </span>
    );
}
